export type MoneyContext = {
  currencyCode?: string | null;
  countryCode?: string | null;
  accountType?: string | null;
};

export type FormatCurrencyOptions = {
  empty?: string;
  notation?: 'standard' | 'compact';
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/** Country code → ISO currency. Used only when currency_code is missing or invalid. */
export const COUNTRY_CURRENCY: Record<string, string> = {
  AE: 'AED',
  AT: 'EUR',
  AU: 'AUD',
  BE: 'EUR',
  BR: 'BRL',
  CA: 'CAD',
  CH: 'CHF',
  DE: 'EUR',
  EG: 'EGP',
  ES: 'EUR',
  FR: 'EUR',
  GB: 'GBP',
  IE: 'EUR',
  IN: 'INR',
  IT: 'EUR',
  JP: 'JPY',
  MX: 'MXN',
  NL: 'EUR',
  PL: 'PLN',
  PT: 'EUR',
  SA: 'SAR',
  SE: 'SEK',
  SG: 'SGD',
  TR: 'TRY',
  UK: 'GBP',
  US: 'USD',
};

/** Currency → locale that carries that currency's grouping and symbol placement. */
const CURRENCY_LOCALE: Record<string, string> = {
  AED: 'en-AE',
  AUD: 'en-AU',
  BRL: 'pt-BR',
  CAD: 'en-CA',
  CHF: 'de-CH',
  EGP: 'ar-EG',
  EUR: 'en-IE',
  GBP: 'en-GB',
  INR: 'en-IN',
  JPY: 'en-US',
  MXN: 'es-MX',
  PLN: 'pl-PL',
  SAR: 'ar-SA',
  SEK: 'sv-SE',
  SGD: 'en-SG',
  TRY: 'tr-TR',
  USD: 'en-US',
};

let activeMoneyContext: MoneyContext = {};

export function setActiveMoneyContext(context: MoneyContext | null | undefined): void {
  activeMoneyContext = {
    currencyCode: context?.currencyCode ?? null,
    countryCode: context?.countryCode ?? null,
    accountType: context?.accountType ?? null,
  };
}

export function getActiveMoneyContext(): MoneyContext {
  return activeMoneyContext;
}

function normalizeCode(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

function contextIsExplicit(context: MoneyContext | undefined): context is MoneyContext {
  return context != null && ('currencyCode' in context || 'countryCode' in context);
}

export function isSupportedCurrency(code: string): boolean {
  if (!/^[A-Z]{3}$/.test(code)) return false;
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: code }).format(0);
    return true;
  } catch {
    return false;
  }
}

export function currencyForCountry(countryCode: string | null | undefined): string | null {
  const country = normalizeCode(countryCode);
  if (!country) return null;
  return COUNTRY_CURRENCY[country] ?? null;
}

export function resolveMoney(
  context?: MoneyContext
): { currency: string; locale: string } | null {
  const source = contextIsExplicit(context) ? context : activeMoneyContext;
  const requested = normalizeCode(source.currencyCode);
  const country = normalizeCode(source.countryCode);

  let currency = '';
  if (requested && isSupportedCurrency(requested)) {
    currency = requested;
  } else {
    currency = currencyForCountry(country) ?? '';
  }
  if (!currency || !isSupportedCurrency(currency)) return null;

  const locale = CURRENCY_LOCALE[currency] ?? 'en';
  return { currency, locale };
}

function plainNumber(value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
  }).format(value);
}

export function formatCurrency(
  value: number | null | undefined,
  context?: MoneyContext,
  options?: FormatCurrencyOptions
): string {
  const empty = options?.empty ?? '—';
  if (value == null || typeof value !== 'number' || !Number.isFinite(value)) return empty;

  const resolved = resolveMoney(context);
  if (!resolved) return plainNumber(value, options?.maximumFractionDigits ?? 2);

  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: resolved.currency,
  };
  if (options?.notation === 'compact') {
    formatOptions.notation = 'compact';
    formatOptions.compactDisplay = 'short';
  }
  if (options?.minimumFractionDigits != null) {
    formatOptions.minimumFractionDigits = options.minimumFractionDigits;
  }
  if (options?.maximumFractionDigits != null) {
    formatOptions.maximumFractionDigits = options.maximumFractionDigits;
  }

  try {
    return new Intl.NumberFormat(resolved.locale, formatOptions).format(value);
  } catch {
    return plainNumber(value, options?.maximumFractionDigits ?? 2);
  }
}

export function currencySymbol(context?: MoneyContext): string | null {
  const resolved = resolveMoney(context);
  if (!resolved) return null;
  try {
    const parts = new Intl.NumberFormat(resolved.locale, {
      style: 'currency',
      currency: resolved.currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? null;
  } catch {
    return null;
  }
}

export function moneyAmountLabel(base: string, context?: MoneyContext): string {
  const symbol = currencySymbol(context);
  return symbol ? `${base} (${symbol})` : base;
}
